import React, { useRef, useEffect, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import ForceGraph3D from 'react-force-graph-3d';
import SpriteText from 'three-spritetext';
import { SphereGeometry, Mesh, Color, MeshPhongMaterial } from 'three';

// A simplified version of the graph component specifically for the Dashboard
const DashboardGraph = ({ graphData = { nodes: [], links: [] }, height = 250 }) => {
  const graphRef = useRef(null);
  const [is3D, setIs3D] = useState(true); // Default to 3D mode
  
  // Generate some sample data if none is provided
  const sampleData = {
    nodes: [
      { id: 'BTC', name: 'Bitcoin', val: 40, color: '#f7931a', tokenImage: 'https://cryptologos.cc/logos/bitcoin-btc-logo.png' },
      { id: 'ETH', name: 'Ethereum', val: 35, color: '#627eea', tokenImage: 'https://cryptologos.cc/logos/ethereum-eth-logo.png' },
      { id: 'E3D', name: 'E3D Token', val: 30, color: '#4d79ff', tokenImage: '/images/e3d-token.png' },
      { id: 'USDT', name: 'Tether', val: 25, color: '#26a17b', tokenImage: 'https://cryptologos.cc/logos/tether-usdt-logo.png' },
      { id: 'DOGE', name: 'Dogecoin', val: 22, color: '#c3a634', tokenImage: 'https://cryptologos.cc/logos/dogecoin-doge-logo.png' },
      { id: 'SHIB', name: 'Shiba Inu', val: 20, color: '#faa401', tokenImage: 'https://cryptologos.cc/logos/shiba-inu-shib-logo.png' },
      { id: 'LINK', name: 'Chainlink', val: 18, color: '#2a5ada', tokenImage: 'https://cryptologos.cc/logos/chainlink-link-logo.png' },
      { id: 'SOL', name: 'Solana', val: 23, color: '#00ffbd', tokenImage: 'https://cryptologos.cc/logos/solana-sol-logo.png' },
      { id: 'UNI', name: 'Uniswap', val: 19, color: '#ff007a', tokenImage: 'https://cryptologos.cc/logos/uniswap-uni-logo.png' },
      { id: 'MATIC', name: 'Polygon', val: 21, color: '#8247e5', tokenImage: 'https://cryptologos.cc/logos/polygon-matic-logo.png' }
    ],
    links: [
      { source: 'BTC', target: 'ETH', value: 0.7 },
      { source: 'ETH', target: 'E3D', value: 0.9 },
      { source: 'E3D', target: 'UNI', value: 0.8 },
      { source: 'ETH', target: 'LINK', value: 0.5 },
      { source: 'ETH', target: 'MATIC', value: 0.6 },
      { source: 'MATIC', target: 'SOL', value: 0.4 },
      { source: 'BTC', target: 'USDT', value: 0.8 },
      { source: 'DOGE', target: 'SHIB', value: 0.7 },
      { source: 'UNI', target: 'USDT', value: 0.6 },
      { source: 'BTC', target: 'DOGE', value: 0.3 }
    ]
  };

  // Use provided graphData or sample data
  const data = graphData.nodes.length > 0 ? graphData : sampleData;

  useEffect(() => {
    // Auto-zoom to fit the graph when rendered
    if (graphRef.current) {
      graphRef.current.zoomToFit(400, 10);
      
      // Start a slow rotation animation
      setTimeout(() => {
        if (is3D && graphRef.current) {
          // Auto-rotate camera
          let angle = 0;
          const distance = 400;
          
          const interval = setInterval(() => {
            if (!graphRef.current) {
              clearInterval(interval);
              return;
            }
            
            angle += Math.PI / 300;
            graphRef.current.cameraPosition({
              x: distance * Math.sin(angle),
              z: distance * Math.cos(angle)
            });
          }, 30);

          return () => clearInterval(interval);
        }
      }, 1000);
    }
  }, [is3D]);

  // Node object generator
  const nodeThreeObject = node => {
    if (!is3D) return null;

    const geometry = new SphereGeometry(node.val || 10);
    const material = new MeshPhongMaterial({ 
      color: node.color || '#4d79ff',
      transparent: true,
      opacity: 0.8
    });
    
    const mesh = new Mesh(geometry, material);
    return mesh;
  };

  // Label generator
  const nodeLabel = node => `${node.name || node.id}`;

  return (
    <div style={{ height, width: '100%', background: '#000', borderRadius: '8px', overflow: 'hidden' }}>
      {is3D ? (
        <ForceGraph3D
          ref={graphRef}
          graphData={data}
          nodeLabel={nodeLabel}
          nodeThreeObject={nodeThreeObject}
          linkWidth={1}
          linkColor={() => '#ffffff'}
          backgroundColor="#000000"
          nodeAutoColorBy="group"
          enableNodeDrag={false}
          enableNavigationControls={false}
          showNavInfo={false}
        />
      ) : (
        <ForceGraph2D
          ref={graphRef}
          graphData={data}
          nodeLabel={nodeLabel}
          nodeColor={node => node.color}
          nodeVal={node => node.val}
          linkWidth={1}
          linkColor={() => '#ffffff'}
          nodeAutoColorBy="group"
          enableNodeDrag={false}
          d3AlphaDecay={0.02}
        />
      )}
    </div>
  );
};

export default DashboardGraph;
